import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { listSEO, upsertSEO, uploadUrl } from '../lib/api';
import { ArrowLeft, Save, Image as ImageIcon, Search } from 'lucide-react';

const PATHS = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About' },
  { path: '/services', label: 'Services' },
  { path: '/services/quality-inspection', label: 'Service — Quality Inspection' },
  { path: '/services/product-testing', label: 'Service — Product Testing' },
  { path: '/services/vendor-audit', label: 'Service — Vendor Audit' },
  { path: '/services/cargo-survey', label: 'Service — Cargo Survey' },
  { path: '/industries', label: 'Industries' },
  { path: '/standards', label: 'Standards' },
  { path: '/why-aqc', label: 'Why AQC' },
  { path: '/resources', label: 'Resources' },
  { path: '/contact', label: 'Contact' },
];

export default function SEOEditor({ onOpenMedia }) {
  const [existing, setExisting] = useState({});
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const list = await listSEO();
      const map = {};
      (list || []).forEach((d) => (map[d.path] = d));
      setExisting(map);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  if (editing) return <SEOForm path={editing} current={existing[editing]} onBack={() => { setEditing(null); load(); }} onOpenMedia={onOpenMedia} />;

  return (
    <div className="bg-white border border-aqc-line rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-2">
        <Search size={18} className="text-aqc-red" />
        <h2 className="font-serif text-2xl text-aqc-navy">SEO Metadata</h2>
      </div>
      <p className="text-sm text-aqc-muted mb-5">Override the title, description, keywords, canonical URL and Open Graph image for any public page. Empty overrides fall back to defaults.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {PATHS.map((p) => {
          const cfg = existing[p.path];
          return (
            <button key={p.path} onClick={() => setEditing(p.path)} className="text-left px-4 py-3 rounded-md border border-aqc-line hover:border-aqc-red hover:bg-aqc-cream transition-colors">
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-aqc-navy">{p.label}</div>
                {cfg ? <span className="text-[10px] uppercase tracking-widest text-aqc-red bg-aqc-red/10 px-2 py-0.5 rounded">custom</span> : <span className="text-[10px] uppercase tracking-widest text-aqc-muted">default</span>}
              </div>
              <div className="text-xs text-aqc-muted mt-0.5 font-mono">{p.path}</div>
              {cfg?.title && <div className="text-xs text-aqc-muted mt-1 line-clamp-1">{cfg.title}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SEOForm({ path, current, onBack, onOpenMedia }) {
  const [f, setF] = useState({
    title: current?.title || '',
    description: current?.description || '',
    keywords: current?.keywords || '',
    og_image: current?.og_image || '',
    canonical: current?.canonical || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });

  const save = async () => {
    setSaving(true);
    try {
      await upsertSEO({ path, ...f });
      toast({ title: 'SEO saved', description: 'Metadata will be applied when the page next loads.' });
      onBack();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again.' });
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-aqc-line rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-aqc-navy"><ArrowLeft size={14} /> Back to SEO list</button>
        <span className="text-xs text-aqc-muted font-mono">{path}</span>
      </div>
      <h2 className="font-serif text-2xl text-aqc-navy">SEO — {path}</h2>
      <div className="mt-5 space-y-4">
        <div>
          <Label>Page title</Label>
          <Input value={f.title} onChange={set('title')} placeholder="e.g. About AQC — Independent Quality Assurance" className="mt-1.5" maxLength={70} />
          <div className="text-[0.72rem] text-aqc-muted mt-1">{f.title.length}/70 characters (Google truncates around 60)</div>
        </div>
        <div>
          <Label>Meta description</Label>
          <Textarea value={f.description} onChange={set('description')} rows={3} placeholder="Concise summary shown in search results." className="mt-1.5" maxLength={200} />
          <div className="text-[0.72rem] text-aqc-muted mt-1">{f.description.length}/200 characters (Google truncates around 155)</div>
        </div>
        <div>
          <Label>Keywords</Label>
          <Input value={f.keywords} onChange={set('keywords')} placeholder="comma, separated, keywords" className="mt-1.5" />
        </div>
        <div>
          <Label>Open Graph image URL</Label>
          <div className="flex gap-2 mt-1.5">
            <Input value={f.og_image} onChange={set('og_image')} placeholder="https://... or /api/uploads/..." />
            <Button type="button" variant="outline" onClick={() => onOpenMedia && onOpenMedia({ onPick: (u) => setF((s) => ({ ...s, og_image: u })) })} className="whitespace-nowrap gap-1.5"><ImageIcon size={14} /> Pick</Button>
          </div>
          {f.og_image && <img src={uploadUrl(f.og_image)} alt="og preview" className="mt-3 w-64 h-32 object-cover rounded-md border border-aqc-line" />}
        </div>
        <div>
          <Label>Canonical URL (optional)</Label>
          <Input value={f.canonical} onChange={set('canonical')} placeholder="Leave empty to use current URL" className="mt-1.5" />
        </div>
      </div>
      <div className="mt-6 flex gap-2">
        <Button onClick={save} disabled={saving} className="bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white gap-1.5"><Save size={14} /> {saving ? 'Saving…' : 'Save SEO metadata'}</Button>
        <Button variant="outline" onClick={onBack}>Cancel</Button>
      </div>
    </div>
  );
}
